// Simple component registry for Page Builder rendering (renamed to .jsx for JSX parsing)
import React from 'react';
import QRCode from 'qrcode';
import i18next from 'i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { usePageMeta } from './PageContext.jsx';
import { toBackendUrl } from '@/lib/utils';
import { api } from '@/api';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

function getCurrentPathWithQueryAndHash(){
  try {
    return `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
  } catch {
    return '/';
  }
}

function buildCustomerLoginHref(){
  const redirect = encodeURIComponent(getCurrentPathWithQueryAndHash());
  return `/customer/login?redirect=${redirect}`;
}

function CodeRequestHeader({ customer, isAuthenticated }){
  const backLabel = i18next.t('public.header.backToLanding', { defaultValue: 'Zur Landingpage' });
  const loginLabel = i18next.t('public.header.login', { defaultValue: 'Anmelden' });
  const loggedInAs = i18next.t('public.header.loggedInAs', {
    email: customer?.email || '',
    defaultValue: customer?.email ? `Angemeldet als ${customer.email}` : 'Angemeldet',
  });

  const primaryColor = 'var(--pb-primary, #2563eb)';

  return (
    <div className="flex items-center justify-between text-xs">
      <a href="/" className="underline opacity-75 hover:opacity-100">
        {backLabel}
      </a>
      {isAuthenticated ? (
        <span className="opacity-75">{loggedInAs}</span>
      ) : (
        <a href={buildCustomerLoginHref()} className="underline font-medium" style={{ color: primaryColor }}>
          {loginLabel}
        </a>
      )}
    </div>
  );
}

// Lightweight context to coordinate modular request form parts
const RequestFormContext = React.createContext(null);
export function RequestFormProvider({ props, children }){
  const { customer, isAuthenticated } = useCustomerAuth();
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [showSuccessAnim, setShowSuccessAnim] = React.useState(false);
  // Gate state: set when a gated dropdown blocks submission
  const [gateBlocked, setGateBlocked] = React.useState(false);
  const [gateBlockedMsg, setGateBlockedMsg] = React.useState('');
  // Map of fieldName -> selected value for dropdown fields (also kept in ref for stale-free access)
  const [dropdownValues, setDropdownValuesState] = React.useState({});
  const dropdownValuesRef = React.useRef({});
  // Map of fieldName -> { requiredValue, errorMsg } for gated dropdown fields
  const dropdownGatesRef = React.useRef({});
  // Map of fieldKey -> { checkFn, errorMsg } for other required fields (name, consent, etc.)
  const requiredChecksRef = React.useRef({});

  function setDropdownValue(fieldName, value){
    dropdownValuesRef.current = { ...dropdownValuesRef.current, [fieldName]: value };
    setDropdownValuesState({ ...dropdownValuesRef.current });
  }
  function registerDropdownGate(fieldName, requiredValue, errorMsg){
    dropdownGatesRef.current[fieldName] = { requiredValue, errorMsg };
  }
  function unregisterDropdownGate(fieldName){
    delete dropdownGatesRef.current[fieldName];
  }
  function registerRequiredCheck(fieldKey, checkFn, errorMsg){
    requiredChecksRef.current[fieldKey] = { checkFn, errorMsg };
  }
  function unregisterRequiredCheck(fieldKey){
    delete requiredChecksRef.current[fieldKey];
  }

  React.useEffect(() => {
    if (!isAuthenticated || !customer) return;
    const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
    if (!email && customer.email) setEmail(customer.email);
    if (!name && fullName) setName(fullName);
  }, [isAuthenticated, customer, email, name]);

  function getScanSlug(){
    try {
      const parts = window.location.pathname.split('/').filter(Boolean);
      const idx = parts.findIndex(p=>p.toLowerCase()==='scan');
      if(idx>=0 && parts[idx+1]) return parts[idx+1];
    } catch {}
    return null;
  }
  const onSubmit = async (e)=>{
    if(e && typeof e.preventDefault === 'function') e.preventDefault();
    if(submitting) return;
    setGateBlocked(false);
    setGateBlockedMsg('');
    // Check other registered required fields (name, consent, etc.)
    for (const [, check] of Object.entries(requiredChecksRef.current)) {
      if (!check.checkFn()) {
        setError(check.errorMsg);
        return;
      }
    }
    // Use ref for stale-free access to current dropdown values and gates
    const currentValues = dropdownValuesRef.current;
    const gates = dropdownGatesRef.current;
    for (const [fieldName, gate] of Object.entries(gates)) {
      if (gate.requiredValue && currentValues[fieldName] !== gate.requiredValue) {
        const msg = gate.errorMsg || `Bitte wähle die Option "${gate.requiredValue}" um fortzufahren.`;
        setGateBlocked(true);
        setGateBlockedMsg(msg);
        return;
      }
    }
    setSubmitting(true);
    setMessage('');
    setError('');
    setShowSuccessAnim(false);
    const slug = getScanSlug();
    try {
      await api.publicScan({ slug, email, name, consentNewsletter: consent });
      setMessage('Vielen Dank! Bitte bestätige die Mail, die du in den nächsten Minuten bekommst.');
      setShowSuccessAnim(true);
    } catch (err){
      setShowSuccessAnim(false);
      setError(err?.message || 'Fehler beim Senden');
    } finally {
      setSubmitting(false);
    }
  };
  const value = { email, setEmail, name, setName, consent, setConsent, submitting, onSubmit, message, error, showSuccessAnim, gateBlocked, gateBlockedMsg, dropdownValues, setDropdownValue, registerDropdownGate, unregisterDropdownGate, registerRequiredCheck, unregisterRequiredCheck };
  return <RequestFormContext.Provider value={value}>{children}</RequestFormContext.Provider>;
}
function useRequestForm(){
  const ctx = React.useContext(RequestFormContext);
  return ctx || { email:'', setEmail:()=>{}, name:'', setName:()=>{}, consent:false, setConsent:()=>{}, submitting:false, onSubmit:()=>{}, message:'', error:'', showSuccessAnim:false, gateBlocked:false, gateBlockedMsg:'', dropdownValues:{}, setDropdownValue:()=>{}, registerDropdownGate:()=>{}, unregisterDropdownGate:()=>{}, registerRequiredCheck:()=>{}, unregisterRequiredCheck:()=>{} };
}
// Renders gate error screen when gate is blocked, otherwise renders children
function GateBoundaryInner({ children }){
  const { gateBlocked, gateBlockedMsg } = useRequestForm();
  if (gateBlocked) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center space-y-3">
        <div className="text-base font-semibold text-red-700">Aktionscode konnte nicht angefordert werden</div>
        {gateBlockedMsg && <p className="text-sm text-red-600">{gateBlockedMsg}</p>}
      </div>
    );
  }
  return <>{children}</>;
}

// Reusable styled button that reads the same props as the 'button' node
function StyledButton({ props, onClick, disabled: disabledOverride }){
  const [hovered, setHovered] = React.useState(false);
  const variant = props?.variant || 'primary';

  // Determine if custom styling is requested
  const hasCustom = !!(props?.background || props?.color || props?.borderColor || props?.borderWidth != null || props?.borderRadius || props?.borderRadiusPx != null || props?.paddingX != null || props?.paddingY != null || props?.hoverBackground || props?.hoverColor || props?.hoverBorderColor || props?.outline || props?.fullWidth);
  const primaryColor = 'var(--pb-primary, #2563eb)';

  function radiusPx(){
    if(typeof props?.borderRadiusPx === 'number') return props.borderRadiusPx;
    const map = { none:0, sm:4, md:6, lg:8, xl:12, full:9999 };
    return map[props?.borderRadius] ?? 6; // default md
  }
  const style = {};
  const isDisabled = (props?.disabled === true || props?.disabled === '1') || !!disabledOverride;
  const hoveredEff = hovered && !isDisabled;
  if(hasCustom){
    // Background & text
    if(isDisabled){
      style.backgroundColor = props?.disabledBackground ?? (props?.outline ? 'transparent' : '#9ca3af');
      style.color = props?.disabledColor ?? (props?.outline ? '#9ca3af' : '#ffffff');
    } else if(props?.outline){
      style.backgroundColor = hoveredEff ? (props?.hoverBackground || 'transparent') : 'transparent';
      style.color = hoveredEff ? (props?.hoverColor || primaryColor) : (props?.color || primaryColor);
    } else {
      style.backgroundColor = hoveredEff ? (props?.hoverBackground || props?.background || (variant==='secondary'?'#e5e7eb':'#2563eb')) : (props?.background || (variant==='secondary'?'#e5e7eb':'#2563eb'));
      style.color = hoveredEff ? (props?.hoverColor || (variant==='secondary'?'#111827':'#ffffff')) : (props?.color || (variant==='secondary'?'#111827':'#ffffff'));
    }
    // Border
    const bw = typeof props?.borderWidth === 'number' ? props.borderWidth : (props?.outline ? 1 : 0);
    style.borderWidth = bw + 'px';
    style.borderStyle = bw>0 ? 'solid' : undefined;
    style.borderColor = isDisabled
      ? (props?.disabledBorderColor ?? (props?.outline ? '#9ca3af' : (props?.borderColor || primaryColor)))
      : (hoveredEff ? (props?.hoverBorderColor || props?.borderColor || primaryColor) : (props?.borderColor || primaryColor));
    // Radius
    style.borderRadius = radiusPx() + 'px';
    // Padding
    if(typeof props?.paddingX === 'number' || typeof props?.paddingY === 'number'){
      const px = typeof props?.paddingX === 'number' ? props.paddingX : 16;
      const py = typeof props?.paddingY === 'number' ? props.paddingY : 8;
      style.padding = `${py}px ${px}px`;
    }
    // Width
    if(props?.fullWidth === true || props?.fullWidth === '1') style.width = '100%';
  }

  const baseCls = 'text-sm font-medium transition disabled:opacity-60 inline-flex items-center justify-center';
  const shapeCls = hasCustom ? '' : 'rounded-md';
  const padCls = hasCustom ? '' : 'px-4 py-2';
  const widthCls = !hasCustom && (props?.fullWidth === true || props?.fullWidth === '1') ? 'w-full' : '';
  const variantCls = hasCustom ? '' : (variant === 'secondary'
    ? 'bg-slate-200 hover:bg-slate-300 text-slate-800'
    : 'bg-blue-600 hover:bg-blue-700 text-white');
  // Shadows
  function shadowCls(kind){
    switch(kind){
      case 'sm': return 'shadow-sm';
      case 'md': return 'shadow';
      case 'lg': return 'shadow-lg';
      case 'xl': return 'shadow-xl';
      default: return '';
    }
  }
  const baseShadow = props?.boxShadow ? shadowCls(props.boxShadow) : '';
  const hoverShadow = props?.hoverBoxShadow ? shadowCls(props.hoverBoxShadow) : '';

  function handleClick(){
    if(isDisabled) return;
    if(typeof onClick === 'function') return onClick();
    if(props?.href) window.location.href = props.href;
  }

  return (
    <button
      className={`${baseCls} ${shapeCls} ${padCls} ${widthCls} ${variantCls} ${baseShadow} ${(!isDisabled && hovered) ? hoverShadow : ''} ${isDisabled ? 'cursor-not-allowed' : ''}`}
      style={hasCustom ? style : undefined}
      onMouseEnter={()=> setHovered(true)}
      onMouseLeave={()=> setHovered(false)}
      onFocus={(e)=>{
        if(props?.focusRingColor || props?.focusRingWidthPx){
          const w = typeof props?.focusRingWidthPx === 'number' ? props.focusRingWidthPx : 0;
          const c = props?.focusRingColor || 'rgba(59,130,246,0.7)';
          e.currentTarget.style.outline = w>0 ? `${w}px solid ${c}` : '';
          e.currentTarget.style.outlineOffset = '2px';
        }
      }}
      onBlur={(e)=>{
        if(props?.focusRingColor || props?.focusRingWidthPx){
          e.currentTarget.style.outline = '';
          e.currentTarget.style.outlineOffset = '';
        }
      }}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {/* Left Icon */}
      {props?.leftIcon && <span style={{ marginRight: (typeof props?.iconGapPx === 'number' ? props.iconGapPx : 8) + 'px' }}>{props.leftIcon}</span>}
      <span dangerouslySetInnerHTML={{ __html: props?.label || 'Button' }} />
      {/* Right Icon */}
      {props?.rightIcon && <span style={{ marginLeft: (typeof props?.iconGapPx === 'number' ? props.iconGapPx : 8) + 'px' }}>{props.rightIcon}</span>}
    </button>
  );
}



export const Registry = {
  countdown: ({ props }) => {
    const { action } = usePageMeta();
    // Resolve target date based on props and available action meta
    const targetSource = props?.targetSource || 'redeemEnd'; // default to redeem end for typical use
    const customTarget = props?.customTarget || '';

    function parseMaybeDate(v){
      if(!v) return null;
      try { const d = new Date(v); return isNaN(d.getTime()) ? null : d; } catch { return null; }
    }

    const targetDate = React.useMemo(()=>{
      // Wenn customTarget direkt gesetzt ist (z.B. in Newslettern), verwende das
      if(customTarget) return parseMaybeDate(customTarget);
      // Sonst action-basiert
      switch(targetSource){
        case 'actionStart': return parseMaybeDate(action?.startsAt);
        case 'actionEnd': return parseMaybeDate(action?.endsAt);
        case 'requestStart': return parseMaybeDate(action?.requestStart);
        case 'requestEnd': return parseMaybeDate(action?.requestEnd);
        case 'redeemStart': return parseMaybeDate(action?.redeemStart);
        case 'redeemEnd': return parseMaybeDate(action?.redeemEnd);
        default: return null;
      }
    }, [targetSource, customTarget, action?.startsAt, action?.endsAt, action?.requestStart, action?.requestEnd, action?.redeemStart, action?.redeemEnd]);

    const [now, setNow] = React.useState(()=> new Date());
    React.useEffect(()=>{
      const interval = setInterval(()=> setNow(new Date()), Math.max(200, Math.min(1000, props?.tickMs ?? 1000)));
      return ()=> clearInterval(interval);
    }, [props?.tickMs]);

    const ms = targetDate ? (targetDate.getTime() - now.getTime()) : 0;
    const negative = ms <= 0;
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const totalMin = Math.max(0, Math.floor(ms / 60000));
    const totalHours = Math.max(0, Math.floor(ms / 3600000));
    const totalDays = Math.max(0, Math.floor(ms / 86400000));

    const showDays = props?.showDays !== false; // default true
    const showHours = props?.showHours !== false; // default true
    const showMinutes = props?.showMinutes !== false; // default true
    const showSeconds = props?.showSeconds === true || (props?.showSeconds == null ? false : !!props?.showSeconds); // default false unless set true

    const enabledUnits = [showDays && 'days', showHours && 'hours', showMinutes && 'minutes', showSeconds && 'seconds'].filter(Boolean);

    let d=0,h=0,m=0,s=0;
    if(enabledUnits.length <= 1){
      // Single unit -> show total remainder in that unit
      if(enabledUnits[0] === 'days') d = totalDays;
      if(enabledUnits[0] === 'hours') h = totalHours;
      if(enabledUnits[0] === 'minutes') m = totalMin;
      if(enabledUnits[0] === 'seconds') s = totalSec;
    } else {
      // Breakdown
      let rem = totalSec;
      if(showDays){ d = Math.floor(rem / 86400); rem -= d*86400; }
      if(showHours){ h = Math.floor(rem / 3600); rem -= h*3600; }
      if(showMinutes){ m = Math.floor(rem / 60); rem -= m*60; }
      if(showSeconds){ s = rem; }
    }

    function pad2(n){ return String(n).padStart(2, '0'); }
    const pad = props?.pad === false ? false : true; // default pad for h/m/s when multi-unit
    const labels = {
      days: props?.labelDays || 'Tage',
      hours: props?.labelHours || 'Stunden',
      minutes: props?.labelMinutes || 'Minuten',
      seconds: props?.labelSeconds || 'Sekunden',
    };
    const showLabels = props?.showLabels !== false; // default true
    const layout = props?.layout || 'blocks'; // 'blocks' | 'inline'
    const sep = props?.separator || ':';
    const endedText = props?.endedText || '';
    const hideWhenEnded = props?.hideWhenEnded === true;
    const prefix = props?.prefix || '';
    const suffix = props?.suffix || '';

    const baseStyle = resolveStyle(props);
    
    // Design Presets
    const preset = props?.preset || '';
    const presets = {
      modern: {
        gradientFrom: '#667eea',
        gradientTo: '#764ba2',
        blockBg: 'white',
        blockBorderColor: '#3b82f6',
        digitColor: '#1e40af',
        labelColor: '#64748b',
        subtextColor: 'rgba(255,255,255,0.9)',
      },
      elegant: {
        gradientFrom: '#0f2027',
        gradientTo: '#2c5364',
        blockBg: 'white',
        blockBorderColor: '#1e40af',
        digitColor: '#1e3a8a',
        labelColor: '#475569',
        subtextColor: 'rgba(255,255,255,0.95)',
      },
      minimal: {
        gradientFrom: '#f8f9fa',
        gradientTo: '#e9ecef',
        blockBg: 'white',
        blockBorderColor: '#cbd5e1',
        digitColor: '#1e293b',
        labelColor: '#64748b',
        subtextColor: '#475569',
      },
      vibrant: {
        gradientFrom: '#ff6b6b',
        gradientTo: '#feca57',
        blockBg: 'white',
        blockBorderColor: '#ff6348',
        digitColor: '#c23616',
        labelColor: '#95a5a6',
        subtextColor: 'rgba(255,255,255,0.95)',
      },
      dark: {
        gradientFrom: '#232526',
        gradientTo: '#414345',
        blockBg: '#1a1a1a',
        blockBorderColor: '#ffd700',
        digitColor: '#ffd700',
        labelColor: '#a0a0a0',
        subtextColor: 'rgba(255,215,0,0.8)',
      },
    };
    
    const presetColors = preset && presets[preset] ? presets[preset] : presets.modern;
    
    // Colors - nutze Preset falls gesetzt, sonst Custom oder Default
    const gradientFrom = props?.gradientFrom || presetColors.gradientFrom;
    const gradientTo = props?.gradientTo || presetColors.gradientTo;
    const blockBg = props?.blockBackground || presetColors.blockBg;
    const blockBorderColor = props?.blockBorderColor || presetColors.blockBorderColor;
    const digitColor = props?.digitColor || presetColors.digitColor;
    const labelColor = props?.labelColor || presetColors.labelColor;
    const subtextColor = props?.subtextColor || presetColors.subtextColor;
    
    const digitSize = typeof props?.digitFontSize === 'number' ? props.digitFontSize : 32;
    const labelSize = typeof props?.labelFontSize === 'number' ? props.labelFontSize : 11;
    const gap = typeof props?.gapPx === 'number' ? props.gapPx : 12;
    const blockRadius = typeof props?.blockRadiusPx === 'number' ? props.blockRadiusPx : 8;
    const blockPadX = typeof props?.blockPadX === 'number' ? props.blockPadX : 16;
    const blockPadY = typeof props?.blockPadY === 'number' ? props.blockPadY : 12;
    const blockMinWidth = typeof props?.blockMinWidthPx === 'number' ? props.blockMinWidthPx : 70;
    
    const blockStyle = {
      background: blockBg,
      border: `2px solid ${blockBorderColor}`,
      borderRadius: blockRadius + 'px',
      padding: `${blockPadY}px ${blockPadX}px`,
      minWidth: blockMinWidth + 'px',
      color: digitColor,
    };

    if(!targetDate){
      // In editor show a hint box
      return (
        <div className={`pb-countdown ${props?.className||''}`} style={baseStyle}>
          <div className="text-[11px] text-slate-500 border rounded px-2 py-1 inline-block bg-slate-50">Kein Zielzeitpunkt vorhanden (Action-Feld leer?)</div>
        </div>
      );
    }
    if(negative){
      if(hideWhenEnded) return null;
      return (
        <div className={`pb-countdown ${props?.className||''}`} style={baseStyle}>
          {endedText ? <div className="text-[12px]" dangerouslySetInnerHTML={{ __html: endedText }} /> : null}
        </div>
      );
    }

    const items = [];
    if(showDays) items.push({ key:'days', value:d });
    if(showHours) items.push({ key:'hours', value:h });
    if(showMinutes) items.push({ key:'minutes', value:m });
    if(showSeconds) items.push({ key:'seconds', value:s });

    function renderValue(unit, val){
      if(layout === 'inline'){
        return (<span className="inline-flex items-baseline"><span style={{ fontSize: digitSize, color: digitColor }} className="tabular-nums">{(pad && unit!=='days' && items.length>1) ? pad2(val) : String(val)}</span>{showLabels ? <span style={{ fontSize: labelSize, color: labelColor }} className="ml-1 text-[10px]">{labels[unit]}</span> : null}</span>);
      }
      return (
        <div className="flex flex-col items-center text-center" style={blockStyle}>
          <div style={{ fontSize: digitSize, color: digitColor }} className="font-bold tabular-nums">{(pad && unit!=='days' && items.length>1) ? pad2(val) : String(val)}</div>
          {showLabels ? <div style={{ fontSize: labelSize, color: labelColor, textTransform: 'uppercase', marginTop: '4px' }}>{labels[unit]}</div> : null}
        </div>
      );
    }

    // Optional Title rendering with position
    const titleHtml = props?.title || '';
    const titlePos = props?.titlePosition || 'above'; // above | below | left | right
    const titleGap = typeof props?.titleGapPx === 'number' ? props.titleGapPx : 8;
    const titleSize = typeof props?.titleFontSize === 'number' ? props.titleFontSize : undefined;
    const titleAlign = props?.titleAlign || 'left'; // left | center | right (for above/below)

    function CountdownCore(){
      // map global textAlign to flex justification for block layout
      const justify = props?.textAlign === 'center' ? 'center' : (props?.textAlign === 'right' ? 'flex-end' : 'flex-start');
      return (
        <>
          {layout === 'inline' ? (
            <div className="inline-flex items-baseline" style={{ gap: (typeof props?.inlineGapPx==='number'? props.inlineGapPx : 6)+'px' }}>
              {items.map((it, idx) => (
                <React.Fragment key={it.key}>
                  {renderValue(it.key, it.value)}
                  {sep && idx < items.length-1 ? <span className="mx-1 opacity-70" aria-hidden>{sep}</span> : null}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="flex items-stretch" style={{ gap: gap+'px', justifyContent: justify }}>
              {items.map(it => <div key={it.key}>{renderValue(it.key, it.value)}</div>)}
            </div>
          )}
        </>
      );
    }

    function Title(){
      if(!titleHtml) return null;
      return <div style={{ fontSize: titleSize }} className="pb-countdown-title" dangerouslySetInnerHTML={{ __html: titleHtml }} />;
    }

    const content = (
      <>
        {prefix ? <div className="mb-1" dangerouslySetInnerHTML={{ __html: prefix }} /> : null}
        {/* Title positioning */}
        {titleHtml && titlePos === 'above' ? (
          <div className={`${titleAlign==='center'?'text-center':titleAlign==='right'?'text-right':'text-left'}`} style={{ marginBottom: titleGap }}><Title /></div>
        ) : null}

        {titleHtml && titlePos === 'left' ? (
          <div className="flex items-center" style={{ gap: titleGap+'px' }}>
            <Title />
            <CountdownCore />
          </div>
        ) : titleHtml && titlePos === 'right' ? (
          <div className="flex items-center" style={{ gap: titleGap+'px' }}>
            <CountdownCore />
            <Title />
          </div>
        ) : (
          <CountdownCore />
        )}

        {titleHtml && titlePos === 'below' ? (
          <div className={`${titleAlign==='center'?'text-center':titleAlign==='right'?'text-right':'text-left'}`} style={{ marginTop: titleGap }}><Title /></div>
        ) : null}

        {suffix ? <div className="mt-1" dangerouslySetInnerHTML={{ __html: suffix }} /> : null}
      </>
    );

    const subtitle = props?.subtitle || '';
    
    return (
      <div className={`pb-countdown ${props?.className||''}`} style={{ 
        ...baseStyle, 
        background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        padding: '24px',
        borderRadius: '12px',
        textAlign: 'center',
        margin: '20px 0'
      }}>
        {content}
        {subtitle && <div style={{ color: subtextColor, fontSize: '12px', marginTop: '16px' }}>{subtitle}</div>}
      </div>
    );
  },
  companyBrand: ({ props }) => {
    const { company } = usePageMeta();
    if(!company) return null;
    const showAddress = props?.showAddress !== false; // default true
    const size = typeof props?.logoSizePx === 'number' ? props.logoSizePx : 40;
    const url = toBackendUrl(company.logoUrl || '');
    const name = company.name || '';
    const addrParts = [company.addressLine1, company.addressLine2, [company.postalCode, company.city].filter(Boolean).join(' '), company.country].filter(Boolean);
    return (
      <div className={`pb-company-brand ${props?.className||''}`} style={{ display:'flex', alignItems:'center', gap:'10px', ...resolveStyle(props) }}>
        {url ? (<img src={url} alt={name||'Logo'} style={{ width:size+'px', height:size+'px', borderRadius:'9999px', objectFit:'cover', border:'1px solid #e5e7eb', background:'#fff' }} />) : null}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <div style={{ fontWeight:600 }}>{name}</div>
          {showAddress && addrParts.length>0 && (
            <div style={{ fontSize:'12px', color:'#64748b' }}>{addrParts.join(', ')}</div>
          )}
        </div>
      </div>
    );
  },
  divider: ({ props }) => {
    const thickness = typeof props?.thicknessPx === 'number' ? props.thicknessPx : 1;
    const lineStyle = props?.style || 'solid';
    const color = props?.color || '#e5e7eb';
    const margin = typeof props?.marginY === 'number' ? `${props.marginY}px 0` : '12px 0';
    const maxW = props?.maxWidthPx ? props.maxWidthPx + 'px' : '100%';
    const align = props?.align || 'center'; // center | left | right
    const hasLabel = !!props?.label;
    const hasIcon = !!props?.icon;
    if(!hasLabel && !hasIcon && !props?.leftIcon && !props?.rightIcon){
      const style = { border: 'none', borderTop: `${thickness}px ${lineStyle} ${color}`, margin, width: maxW, ...resolveStyle(props) };
      return <hr className={`pb-divider ${props?.className||''}`} style={style} />;
    }
    const labelStyle = {
      color: props?.labelColor || 'inherit',
      background: props?.labelBackground || 'transparent',
      padding: `${typeof props?.labelPaddingY==='number'?props.labelPaddingY:0}px ${typeof props?.labelPaddingX==='number'?props.labelPaddingX:6}px`,
      fontSize: typeof props?.labelFontSize==='number'? props.labelFontSize+'px' : undefined,
      whiteSpace: 'nowrap'
    };
    const lineCommon = { borderTop: `${thickness}px ${lineStyle} ${color}`, height: 0 };
    const leftFlex = align === 'right' ? 0 : 1;
    const rightFlex = align === 'left' ? 0 : 1;
    const capStyle = {
      color: props?.capColor || (props?.labelColor || 'inherit'),
      fontSize: typeof props?.capFontSize==='number'? props.capFontSize+'px' : (typeof props?.labelFontSize==='number'? props.labelFontSize+'px' : undefined),
    };
    return (
      <div className={`pb-divider has-label ${props?.className||''}`} style={{ margin, width: maxW, ...resolveStyle(props) }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ ...lineCommon, flex: leftFlex }} />
          <div style={{ display:'inline-flex', alignItems:'center', gap:'6px' }}>
            {props?.leftIcon ? <span style={capStyle}>{props.leftIcon}</span> : null}
            <div style={labelStyle}>
              {hasLabel ? (
                <span dangerouslySetInnerHTML={{ __html: props.label }} />
              ) : (hasIcon ? props.icon : '')}
            </div>
            {props?.rightIcon ? <span style={capStyle}>{props.rightIcon}</span> : null}
          </div>
          <div style={{ ...lineCommon, flex: rightFlex }} />
        </div>
      </div>
    );
  },
  spacer: ({ props }) => {
    const h = typeof props?.heightPx === 'number' ? props.heightPx : 16;
    const hSm = typeof props?.mobileHeightPx === 'number' ? props.mobileHeightPx : undefined;
  const hSm640 = typeof props?.tabletHeightPx === 'number' ? props.tabletHeightPx : undefined; // legacy name kept
  const hMd768 = typeof props?.mdHeightPx === 'number' ? props.mdHeightPx : undefined;
    const hLg = typeof props?.desktopHeightPx === 'number' ? props.desktopHeightPx : undefined;
    // Build a class and a small scoped style for responsive heights
    const key = [h||'',hSm||'',hSm640||'',hMd768||'',hLg||''].join('-').replace(/[^0-9-]/g,'');
    const cls = `pb-spacer-${key}`;
    const baseStyle = { height: (hSm ?? h) + 'px', width:'100%', ...resolveStyle(props) };
    return (
      <>
        {(hSm640 || hMd768 || hLg) && (
          <style>{`
            @media (min-width: 640px){ .${cls}{ height: ${hSm640 ?? (hSm ?? h)}px; } }
            @media (min-width: 768px){ .${cls}{ height: ${hMd768 ?? (hSm640 ?? (hSm ?? h))}px; } }
            @media (min-width: 1024px){ .${cls}{ height: ${hLg ?? (hMd768 ?? (hSm640 ?? (hSm ?? h)))}px; } }
          `}</style>
        )}
        <div className={`pb-spacer ${cls} ${props?.className||''}`} style={baseStyle} aria-hidden="true" />
      </>
    );
  },
  row: ({ children, props }) => {
    const gap = typeof props?.gapPx === 'number' ? props.gapPx : 8;
    function ai(v){
      switch(v){
        case 'start': return 'flex-start';
        case 'center': return 'center';
        case 'end': return 'flex-end';
        case 'stretch': return 'stretch';
        default: return 'center';
      }
    }
    function jc(v){
      switch(v){
        case 'start': return 'flex-start';
        case 'center': return 'center';
        case 'end': return 'flex-end';
        case 'between': return 'space-between';
        case 'around': return 'space-around';
        case 'evenly': return 'space-evenly';
        default: return 'flex-start';
      }
    }
    const style = {
      display: 'flex',
      gap: `${gap}px`,
      alignItems: ai(props?.align),
      justifyContent: jc(props?.justify),
      flexWrap: props?.wrap === true || props?.wrap === '1' ? 'wrap' : 'nowrap',
      ...resolveStyle(props),
    };
    return <div className={`pb-row ${props?.className||''}`} style={style}>{children}</div>;
  },
  banner: ({ props }) => {
  const src = toBackendUrl(props?.src || '');
    const isVideo = props?.type === 'video' || /\.mp4($|\?)/i.test(src);
    const style = {
      ...(props?.maxWidthPx ? { maxWidth: props.maxWidthPx + 'px' } : {}),
      ...(props?.borderRadiusPx ? { borderRadius: props.borderRadiusPx + 'px' } : {}),
      ...(props?.heightPx ? { height: props.heightPx + 'px' } : {}),
      ...(props?.background ? { background: props.background } : {}),
      overflow: 'hidden',
    };
    const fit = props?.objectFit || 'cover';
    if (!src) return <div className="w-full bg-slate-100 text-slate-500 text-xs p-4 rounded">Kein Banner gesetzt</div>;
    const handleVideoClick = (ev) => {
      try {
        const v = ev.currentTarget;
        if (v.paused) v.play(); else v.pause();
      } catch(_) {}
    };
    return (
      <div className={`pb-banner w-full ${props?.className||''}`} style={style}>
        {isVideo ? (
          <video
            src={src}
            style={{ width: '100%', height: '100%', objectFit: fit, display:'block' }}
            playsInline
            {...(props?.autoplay ? { autoPlay: true } : {})}
            {...(props?.muted ? { muted: true } : {})}
            {...(props?.loop ? { loop: true } : {})}
            // Default to showing controls unless explicitly disabled ("0")
            controls={props?.controls === '0' ? false : true}
            onClick={handleVideoClick}
          />
        ) : (
          <img src={src} alt={props?.alt || ''} style={{ width: '100%', height: '100%', objectFit: fit, display:'block' }} />
        )}
      </div>
    );
  },
  
  image: ({ props }) => {
    const src = toBackendUrl(props?.src || '');
    const alt = props?.alt || 'Bild';
    const maxWidth = props?.maxWidth || '100%';
    const align = props?.align || 'center';
    const borderRadius = props?.borderRadius || '0';
    const href = props?.href || '';
    
    const alignmentMap = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end'
    };
    
    if (!src) {
      return (
        <div style={{ display: 'flex', justifyContent: alignmentMap[align] || 'center', width: '100%', padding: '20px 0' }}>
          <div style={{ 
            width: '200px',
            height: '150px',
            backgroundColor: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            fontSize: '12px',
            borderRadius
          }}>Bild hinzufügen</div>
        </div>
      );
    }
    
    const imgEl = (
      <img 
        src={src} 
        alt={alt} 
        style={{ 
          maxWidth, 
          height: 'auto',
          borderRadius,
          display: 'block'
        }} 
      />
    );
    return (
      <div style={{ display: 'flex', justifyContent: alignmentMap[align] || 'center', width: '100%', padding: '10px 0' }}>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
            {imgEl}
          </a>
        ) : imgEl}
      </div>
    );
  },
  
  video: ({ props }) => {
    const src = toBackendUrl(props?.src || '');
    const maxWidth = props?.maxWidth || '600px';
    const controls = props?.controls !== false;
    const autoplay = props?.autoplay === true;
    const loop = props?.loop === true;
    const muted = props?.muted === true;
    const align = props?.align || 'center';
    const poster = toBackendUrl(props?.poster || '');
    const { emailMode } = usePageMeta();
    
    const alignmentMap = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end'
    };
    
    if (!src) {
      return (
        <div style={{ display: 'flex', justifyContent: alignmentMap[align] || 'center', width: '100%', padding: '20px 0' }}>
          <div style={{ 
            width: '100%',
            maxWidth,
            height: '200px',
            backgroundColor: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            fontSize: '12px'
          }}>Video hinzufügen</div>
        </div>
      );
    }
    
    // Email-safe fallback: render poster/thumb with play overlay linking to video URL
    if (emailMode) {
      return (
        <div style={{ display: 'flex', justifyContent: alignmentMap[align] || 'center', width: '100%', padding: '10px 0' }}>
          <a href={src} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', maxWidth, position: 'relative' }}>
            <img 
              src={poster || 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'800\' height=\'450\'><rect width=\'100%\' height=\'100%\' fill=\'%23e2e8f0\'/></svg>'}
              alt={props?.alt || 'Video'}
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: props?.borderRadius || 0 }}
            />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '9999px', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 0, height: 0, borderLeft: '18px solid white', borderTop: '12px solid transparent', borderBottom: '12px solid transparent', marginLeft: 4 }} />
              </div>
            </div>
          </a>
        </div>
      );
    }

    // Default interactive video for non-email contexts
    return (
      <div style={{ display: 'flex', justifyContent: alignmentMap[align] || 'center', width: '100%', padding: '10px 0' }}>
        <video 
          style={{ width: '100%', maxWidth, height: 'auto' }} 
          controls={controls}
          autoPlay={autoplay}
          loop={loop}
          muted={muted}
          playsInline
          poster={poster || undefined}
        >
          <source src={src} type="video/mp4" />
          Ihr Browser unterstützt keine Videos.
        </video>
      </div>
    );
  },
  
  newsletterFooter: ({ props }) => {
    const backgroundColor = props?.backgroundColor || '#f8f9fa';
    const textColor = props?.textColor || '#6c757d';
    const linkColor = props?.linkColor || '#007bff';
    const padding = props?.padding || '30px 20px';
    const fontSize = props?.fontSize || '12px';
    const companyName = props?.companyName || i18next.t('common.pagebuilder.defaultCompanyName');
    const companyAddress = props?.companyAddress || i18next.t('common.pagebuilder.defaultCompanyAddress');
    
    return (
      <div style={{ 
        backgroundColor, 
        padding, 
        textAlign: 'center',
        fontSize,
        lineHeight: '1.6',
        color: textColor,
        borderTop: '1px solid #dee2e6'
      }}>
        <div style={{ marginBottom: '15px' }}>
          <strong>{companyName}</strong>
          <br />
          {companyAddress}
        </div>
        <div style={{ marginBottom: '15px', color: textColor }}>
          Sie erhalten diese E-Mail, weil Sie sich für unseren Newsletter angemeldet haben.
        </div>
        <div>
          <a 
            href="{{unsubscribe_url}}" 
            style={{ color: linkColor, textDecoration: 'underline' }}
          >
            Vom Newsletter abmelden
          </a>
        </div>
      </div>
    );
  },
  
  companyLogo: ({ props }) => {
    const { company } = usePageMeta();
    const size = typeof props?.sizePx === 'number' ? props.sizePx : 40;
    const br = typeof props?.borderRadiusPx === 'number' ? props.borderRadiusPx : (props?.rounded ? 9999 : 8);
  const url = toBackendUrl(company?.logoUrl || '');
    if (!url) return <div className="text-[11px] text-slate-500">Kein Logo hinterlegt</div>;
    return (
      <img src={url} alt={company?.name || 'Logo'} style={{ width: size+'px', height: size+'px', borderRadius: br+'px', objectFit: 'cover', display:'block' }} />
    );
  },

  socials: ({ props }) => {
    const iconSize = props?.iconSize || 32;
    const gap = props?.gap || 12;
    const align = props?.align || 'center';
    const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
    
    const links = [
      { key: 'facebook', url: props?.facebookUrl, label: 'Facebook', color: '#1877F2', icon: 'f' },
      { key: 'twitter', url: props?.twitterUrl, label: 'Twitter/X', color: '#1DA1F2', icon: '𝕏' },
      { key: 'instagram', url: props?.instagramUrl, label: 'Instagram', color: '#E4405F', icon: 'IG' },
      { key: 'linkedin', url: props?.linkedinUrl, label: 'LinkedIn', color: '#0A66C2', icon: 'in' },
      { key: 'youtube', url: props?.youtubeUrl, label: 'YouTube', color: '#FF0000', icon: '▶' },
    ].filter(s => s.url);

    if (!links.length) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>Social Media Links konfigurieren</div>;
    }

    return (
      <div style={{ display: 'flex', justifyContent: justifyMap[align], gap: gap + 'px', padding: '10px 0' }}>
        {links.map(social => (
          <a 
            key={social.key} 
            href={social.url} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: iconSize + 'px',
              height: iconSize + 'px',
              borderRadius: '50%',
              backgroundColor: social.color,
              color: '#fff',
              textDecoration: 'none',
              fontSize: (iconSize * 0.5) + 'px',
              fontWeight: 'bold'
            }}
            title={social.label}
          >
            {social.icon}
          </a>
        ))}
      </div>
    );
  },

  columns1: ({ children, props }) => (
    <div className="pb-columns" style={{ width: '100%', ...resolveStyle(props) }}>
      {children}
    </div>
  ),

  columns2: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[1]}</div>
      </div>
    );
  },

  columns2_2575: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[1]}</div>
      </div>
    );
  },

  columns2_7525: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[1]}</div>
      </div>
    );
  },

  columns3: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[1]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[2]}</div>
      </div>
    );
  },

  columns3_252550: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[1]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[2]}</div>
      </div>
    );
  },

  columns3_255025: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[1]}</div>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[2]}</div>
      </div>
    );
  },

  columns3_502525: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[1]}</div>
        <div style={{ flex: '0 0 25%', minWidth: 0 }}>{children?.[2]}</div>
      </div>
    );
  },

  columns4: ({ children, props }) => {
    const gap = props?.gap || 16;
    return (
      <div className="pb-columns" style={{ display: 'flex', gap: gap + 'px', width: '100%', ...resolveStyle(props) }}>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[0]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[1]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[2]}</div>
        <div style={{ flex: '1', minWidth: 0 }}>{children?.[3]}</div>
      </div>
    );
  },
  card: ({ children, props }) => (
    <div className={`pb-card bg-white shadow rounded p-6 w-full max-w-md ${props?.className||''}`} style={resolveStyle(props)}>
      {children}
    </div>
  ),
  section: ({ children, props }) => (
    <section className={`pb-section ${props?.className || ''}`} style={resolveStyle(props)}>
      {children}
    </section>
  ),
  container: ({ children, props }) => (
    <div className={`pb-container mx-auto ${props?.maxWidth === 'md' ? 'max-w-2xl' : props?.maxWidth === 'lg' ? 'max-w-5xl' : 'max-w-3xl'} px-4`} style={resolveStyle(props)}>
      {children}
    </div>
  ),
  heading: ({ props, editable, onUpdate }) => {
    const Tag = `h${props?.level || 2}`;
    const ref = React.useRef(null);
    const [isEditing, setIsEditing] = React.useState(false);
    
    const handleBlur = () => {
      if (ref.current && editable && onUpdate) {
        const newText = ref.current.innerHTML;
        if (newText !== props?.text) {
          onUpdate({ ...props, text: newText });
        }
      }
      setIsEditing(false);
    };

    const handleDoubleClick = (e) => {
      if (editable) {
        e.stopPropagation();
        setIsEditing(true);
        setTimeout(() => {
          if (ref.current) {
            ref.current.focus();
            const range = document.createRange();
            range.selectNodeContents(ref.current);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        }, 0);
      }
    };

    return (
      <Tag 
        ref={ref}
        className={`font-semibold mb-4 text-slate-800 ${editable && isEditing ? 'outline-2 outline-dashed outline-blue-400 bg-blue-50/30' : ''}`}
        style={resolveStyle(props)}
        contentEditable={editable && isEditing}
        suppressContentEditableWarning
        onBlur={handleBlur}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{ __html: props?.text || '' }}
      />
    );
  },
  text: ({ props, editable, onUpdate }) => {
    const ref = React.useRef(null);
    const [isEditing, setIsEditing] = React.useState(false);
    
    const handleBlur = () => {
      if (ref.current && editable && onUpdate) {
        const newText = ref.current.innerHTML;
        if (newText !== props?.text) {
          onUpdate({ ...props, text: newText });
        }
      }
      setIsEditing(false);
    };

    const handleDoubleClick = (e) => {
      if (editable) {
        e.stopPropagation();
        setIsEditing(true);
        setTimeout(() => {
          if (ref.current) {
            ref.current.focus();
            const range = document.createRange();
            range.selectNodeContents(ref.current);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        }, 0);
      }
    };

    return (
      <div 
        ref={ref}
        className={`prose prose-sm max-w-none mb-4 ${editable && isEditing ? 'outline-2 outline-dashed outline-blue-400 bg-blue-50/30' : ''}`}
        contentEditable={editable && isEditing}
        suppressContentEditableWarning
        onBlur={handleBlur}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{ __html: props?.text || '' }}
      />
    );
  },
  button: ({ props }) => <StyledButton props={props} />,
  codeRequestForm: ({ props }) => {
    const { customer, isAuthenticated } = useCustomerAuth();
    const [submitting, setSubmitting] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [name, setName] = React.useState('');
    const [consent, setConsent] = React.useState(false);
    const [msg, setMsg] = React.useState('');
    const [err, setErr] = React.useState('');
    const [showSuccessAnim, setShowSuccessAnim] = React.useState(false);

    React.useEffect(() => {
      if (!isAuthenticated || !customer) return;
      const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
      if (!email && customer.email) setEmail(customer.email);
      if (!name && fullName) setName(fullName);
    }, [isAuthenticated, customer, email, name]);

    function getScanSlug(){
      try {
        const parts = window.location.pathname.split('/').filter(Boolean);
        const idx = parts.findIndex(p=>p.toLowerCase()==='scan');
        if(idx>=0 && parts[idx+1]) return parts[idx+1];
      } catch {}
      return null;
    }
    async function onSubmit(e){
      e.preventDefault();
      if(submitting) return;
      // Manual validation (noValidate would suppress browser bubbles)
      if (!email || !email.trim()) {
        setErr('Bitte gib deine E-Mail-Adresse ein.');
        return;
      }
      if (props?.showName && props?.nameRequired && (!name || !name.trim())) {
        setErr('Bitte gib deinen Namen ein.');
        return;
      }
      if (props?.showConsent && (props?.consentRequired === true || props?.consentRequired === '1') && !consent) {
        setErr('Bitte stimme den Bedingungen zu.');
        return;
      }
      setSubmitting(true);
      setMsg('');
      setErr('');
      setShowSuccessAnim(false);
      try {
        const slug = getScanSlug();
        await api.publicScan({ slug, email, name, consentNewsletter: consent });
        setMsg('Vielen Dank! Bitte bestätige die Mail, die du in den nächsten Minuten bekommst.');
        setShowSuccessAnim(true);
      } catch(ex){
        setShowSuccessAnim(false);
        setErr(ex?.message || 'Fehler beim Senden');
      } finally {
        setSubmitting(false);
      }
    }
    const inputBase = "border px-3 py-2 rounded w-full text-sm";
    return (
      <form onSubmit={onSubmit} className="space-y-3" style={resolveStyle(props)} noValidate>
        <div>
          <input
            value={email}
            onChange={e=>setEmail(e.target.value)}
            type="email"
            className={inputBase}
            placeholder={props?.placeholderEmail || props?.emailLabel || props?.placeholder || 'E-Mail'}
          />
        </div>
        {props?.showName && (
          <div>
            <input
              value={name}
              onChange={e=>setName(e.target.value)}
              type="text"
              className={inputBase}
              placeholder={props?.placeholderName || props?.nameLabel || props?.placeholder || 'Name'}
            />
          </div>
        )}
        {props?.showConsent && (
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} />
            <span>{props?.consentLabel || 'Newsletter Einwilligung'}</span>
          </label>
        )}
        <button disabled={submitting} type="submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded text-sm font-medium">
          <span dangerouslySetInnerHTML={{ __html: (submitting ? (props?.submitLabelLoading || 'Sendet...') : (props?.submitLabel || 'E-Mail bestätigen')) }} />
        </button>
        {showSuccessAnim && (
          <div className="rounded border border-green-200 bg-green-50 p-3 text-center">
            <div className="mx-auto w-20 h-20">
              <DotLottieReact
                src="https://lottie.host/77c4413f-852f-4b5f-80a7-e1863df7fac9/RaxkN1xThE.lottie"
                autoplay
                loop
              />
            </div>
            <p className="text-xs font-medium text-green-800 -mt-1">Vielen Dank! Bitte bestätige die Mail, die du in den nächsten Minuten bekommst.</p>
          </div>
        )}
        {!showSuccessAnim && msg && <p className="text-[11px] text-green-700">{msg}</p>}
        {err && <p className="text-[11px] text-red-600">{err}</p>}
        {props?.disclaimer && <p className="text-[11px] text-slate-500" dangerouslySetInnerHTML={{ __html: props.disclaimer }} />}
      </form>
    );
  },
  // --- Modular Request Form (group + individual parts) ---
  codeRequestGroup: ({ children, props }) => {
    // No inner RequestFormProvider - all codeRequest* parts share the single outer provider
    // from PageRenderer so gates, email state, and submit all live in ONE context.
    return <GateBoundaryInner>{children}</GateBoundaryInner>;
  },
  codeRequestName: ({ props }) => {
    const { name, setName, registerRequiredCheck, unregisterRequiredCheck, gateBlocked } = useRequestForm();
    const nameRef = React.useRef(name);
    React.useEffect(() => { nameRef.current = name; }, [name]);
    React.useEffect(() => {
      if (props?.required) {
        registerRequiredCheck('name', () => !!nameRef.current.trim(), 'Bitte gib deinen Namen ein.');
      } else {
        unregisterRequiredCheck('name');
      }
      return () => unregisterRequiredCheck('name');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.required]);
    if(props?.hidden) return null;
    if(gateBlocked) return null;
    return (
      <div className="space-y-1" style={resolveStyle(props)}>
        {props?.label && (
          <label className="block text-xs font-medium">
            <span dangerouslySetInnerHTML={{ __html: props.label }} />
            {props?.required ? <span className="text-amber-600 ml-1">(Pflicht)</span> : null}
          </label>
        )}
        <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder={props?.placeholder || 'Name'} className="w-full border rounded px-2 py-1 text-sm" />
      </div>
    );
  },
  codeRequestEmail: ({ props }) => {
    const { email, setEmail, registerRequiredCheck, unregisterRequiredCheck, gateBlocked } = useRequestForm();
    const emailRef = React.useRef(email);
    const isRequired = props?.required !== false && props?.required !== '0';
    React.useEffect(() => { emailRef.current = email; }, [email]);
    React.useEffect(() => {
      if (isRequired) {
        const labelText = props?.label ? props.label.replace(/<[^>]*>/g, '').trim() : 'E-Mail';
        registerRequiredCheck('email', () => !!emailRef.current.trim(), `Bitte gib deine ${labelText}-Adresse ein.`);
      } else {
        unregisterRequiredCheck('email');
      }
      return () => unregisterRequiredCheck('email');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRequired, props?.label]);
    if(gateBlocked) return null;
    return (
      <div className="space-y-1" style={resolveStyle(props)}>
        {props?.label && (
          <label className="block text-xs font-medium">
            <span dangerouslySetInnerHTML={{ __html: props.label }} /> {isRequired ? <span className="text-amber-600 ml-1">(Pflicht)</span> : null}
          </label>
        )}
        <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={props?.placeholder || 'you@example.com'} className="w-full border rounded px-2 py-1 text-sm" />
      </div>
    );
  },
  codeRequestConsent: ({ props }) => {
    const { consent, setConsent, registerRequiredCheck, unregisterRequiredCheck, gateBlocked } = useRequestForm();
    const consentRef = React.useRef(consent);
    React.useEffect(() => { consentRef.current = consent; }, [consent]);
    React.useEffect(() => {
      if (props?.required) {
        const msg = props?.requiredError || 'Bitte stimme den Bedingungen zu.';
        registerRequiredCheck('consent', () => consentRef.current === true, msg);
      } else {
        unregisterRequiredCheck('consent');
      }
      return () => unregisterRequiredCheck('consent');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.required, props?.requiredError]);
    if(!props?.label) return null;
    if(gateBlocked) return null;
    return (
      <label className="flex items-start gap-2 text-[11px] select-none" style={resolveStyle(props)}>
        <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} className="mt-[2px]" />
        <span>
          <span dangerouslySetInnerHTML={{ __html: props.label }} />{props?.required ? <span className="text-amber-600 ml-1">(Pflicht)</span> : null}
        </span>
      </label>
    );
  },
  codeRequestDropdown: ({ props }) => {
    const { dropdownValues, setDropdownValue, registerDropdownGate, unregisterDropdownGate, registerRequiredCheck, unregisterRequiredCheck, gateBlocked } = useRequestForm();
    const fieldName = props?.fieldName || 'dropdown';
    const gateSubmit = props?.gateSubmit === true || props?.gateSubmit === '1';
    const requiredValue = props?.requiredValue || '';
    const isRequired = props?.required === true || props?.required === '1';

    // Parse options: stored as JSON string array of { label, value } or plain strings
    const options = React.useMemo(() => {
      const raw = props?.options || '[]';
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map(o => (typeof o === 'string' ? { label: o, value: o } : o));
      } catch {
        return [];
      }
    }, [props?.options]);

    const selected = dropdownValues[fieldName] || '';
    const selectedRef = React.useRef(selected);
    React.useEffect(() => { selectedRef.current = selected; }, [selected]);

    // Register/unregister gate (specific value required)
    React.useEffect(() => {
      if (gateSubmit && requiredValue) {
        const errorMsg = props?.gateErrorMsg || `Bitte wähle "${requiredValue}" um fortzufahren.`;
        registerDropdownGate(fieldName, requiredValue, errorMsg);
      } else {
        unregisterDropdownGate(fieldName);
      }
      return () => unregisterDropdownGate(fieldName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldName, gateSubmit, requiredValue, props?.gateErrorMsg]);

    // Register/unregister simple required check (any non-empty selection) when not using gate
    React.useEffect(() => {
      if (isRequired && !gateSubmit) {
        const plainLabel = props?.label ? props.label.replace(/<[^>]*>/g, '').trim() : 'Auswahl';
        const errorMsg = `Bitte triff eine Auswahl bei "${plainLabel}".`;
        registerRequiredCheck(`dropdown_${fieldName}`, () => !!selectedRef.current, errorMsg);
      } else {
        unregisterRequiredCheck(`dropdown_${fieldName}`);
      }
      return () => unregisterRequiredCheck(`dropdown_${fieldName}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldName, isRequired, gateSubmit, props?.label]);

    if(gateBlocked) return null;

    return (
      <div className="space-y-1" style={resolveStyle(props)}>
        {props?.label && (
          <label className="block text-xs font-medium">
            <span dangerouslySetInnerHTML={{ __html: props.label }} />
            {isRequired ? <span className="text-amber-600 ml-1">(Pflicht)</span> : null}
          </label>
        )}
        <select
          className="w-full border rounded px-2 py-1.5 text-sm bg-white"
          value={selected}
          onChange={e => setDropdownValue(fieldName, e.target.value)}
        >
          <option value="">{props?.placeholder || 'Bitte wählen...'}</option>
          {options.map((o, i) => (
            <option key={i} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  },
  codeRequestSubmit: ({ props }) => {
    const { submitting, onSubmit, message, error, showSuccessAnim, gateBlocked, gateBlockedMsg } = useRequestForm();
    const label = submitting ? (props?.submitLabelLoading || 'Sende...') : (props?.submitLabel || 'Code anfordern');
    // Default to full width for submit if not explicitly set
    const btnProps = { ...props, label, fullWidth: (props?.fullWidth ?? '1') };
    if (gateBlocked) {
      return (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-6 text-center space-y-3">
          <div className="text-base font-semibold text-red-700">Aktionscode konnte nicht angefordert werden</div>
          {gateBlockedMsg ? <div className="text-sm text-red-600">{gateBlockedMsg}</div> : null}
        </div>
      );
    }
    return (
      <div>
        <StyledButton props={btnProps} onClick={onSubmit} disabled={submitting} />
        {showSuccessAnim ? (
          <div className="mt-2 rounded border border-green-200 bg-green-50 p-3 text-center">
            <div className="mx-auto w-20 h-20">
              <DotLottieReact
                src="https://lottie.host/77c4413f-852f-4b5f-80a7-e1863df7fac9/RaxkN1xThE.lottie"
                autoplay
                loop
              />
            </div>
            <div className="text-xs font-medium text-green-800 -mt-1">Vielen Dank! Bitte bestätige die Mail, die du in den nächsten Minuten bekommst.</div>
          </div>
        ) : null}
        {!showSuccessAnim && message ? <div className="mt-1 text-[11px] text-green-700">{message}</div> : null}
        {error ? <div className="mt-1 text-[11px] text-red-600">{error}</div> : null}
      </div>
    );
  },
  // --- /Modular Request Form ---
  codeDisplay: ({ props }) => {
    const [qr, setQr] = React.useState(null);

    // Determine if we're inside the visual builder
    const isEditor = typeof document !== 'undefined' && !!document.querySelector('.pb-editable-root');

    // Determine code from props or URL (supports /claim/:code and ?code=...).
    // In editor, fall back to a demo code to show QR by default.
    const code = React.useMemo(() => {
      if (props?.code) return String(props.code);
      try {
        const url = new URL(window.location.href);
        const qp = url.searchParams.get('code');
        if (qp) return qp;
        const parts = url.pathname.split('/').filter(Boolean);
        const claimIdx = parts.findIndex(p => p.toLowerCase() === 'claim');
        if (claimIdx >= 0 && parts[claimIdx + 1]) return parts[claimIdx + 1];
      } catch {}
      return isEditor ? 'ABCD-1234' : '';
    }, [props?.code, isEditor]);

    React.useEffect(() => {
      let cancelled = false;
      (async () => {
        if (!code) { setQr(null); return; }
        try {
          const redeemUrl = `${window.location.origin}/redeem/${encodeURIComponent(code)}`;
          const dataUrl = await QRCode.toDataURL(redeemUrl, {
            margin: typeof props?.qrMargin === 'number' ? props.qrMargin : 1,
            scale: typeof props?.qrScale === 'number' ? props.qrScale : 6,
            errorCorrectionLevel: 'M',
          });
          if (!cancelled) setQr({ dataUrl, redeemUrl });
        } catch {
          if (!cancelled) setQr(null);
        }
      })();
      return () => { cancelled = true; };
    }, [code, props?.qrScale, props?.qrMargin]);

    function flagOn(v, defaultOn=true){
      if(v === false || v === '0' || v === 0) return false;
      if(v === true || v === '1' || v === 1) return true;
      if(v === '' || v == null) return !!defaultOn;
      return !!v;
    }

    const alignCls = props?.textAlign === 'center' ? 'text-center' : '';
    const style = resolveStyle(props);
    const showCopy = flagOn(props?.showCopyLink, true);
    const copyLabel = props?.copyLabel || 'Link kopieren';
    const showQr = flagOn(props?.showQr, true);

  const title = props?.heading || 'Ihr Code';
    const hint = props?.hint != null && props?.hint !== '' ? props.hint : (props?.note || '');

    return (
      <div className={`space-y-2 bg-green-50 border border-green-200 p-4 rounded-md mb-4 ${alignCls}`} style={style}>
  <div className="text-xs text-slate-600" dangerouslySetInnerHTML={{ __html: title }} />
        <div className="text-2xl font-mono tracking-wide break-all">{code || (isEditor ? (props?.code || 'ABCD-1234') : (props?.code || ''))}</div>
        {showQr && qr && (
          <div className="space-y-1">
            <img src={qr.dataUrl} alt="QR" className="mx-auto border p-1 bg-white" />
            {showCopy && (
              <button
                type="button"
                onClick={() => qr?.redeemUrl && navigator.clipboard?.writeText(qr.redeemUrl)}
                className="underline text-blue-600 text-xs"
                dangerouslySetInnerHTML={{ __html: copyLabel }}
              />
            )}
          </div>
        )}
        {hint && <p className="text-[11px] text-slate-500" dangerouslySetInnerHTML={{ __html: hint }} />}
      </div>
    );
  },
  // Email personalisation: {{code}} token visual
  emailCode: ({ props }) => {
    const style = resolveStyle(props);
    const isEditor = typeof document !== 'undefined' && !!document.querySelector('.pb-editable-root');
    const demoCode = props?.code || 'ABCD-1234';
    const alignCls = props?.textAlign === 'center' ? 'text-center' : props?.textAlign === 'right' ? 'text-right' : '';
    return (
      <div className={`mb-4 ${alignCls}`} style={style}>
        <span className="inline-block font-mono text-base bg-slate-100 border border-slate-200 rounded px-2 py-1">
          {isEditor ? demoCode : (props?.code || demoCode)}
        </span>
        <div className="mt-1 text-[10px] text-slate-500">Platzhalter für {'{{code}}'} in E-Mails</div>
      </div>
    );
  },
  // Email personalisation: QR-Code with fallback
  emailQr: ({ props }) => {
    const [qr, setQr] = React.useState(null);
    const [error, setError] = React.useState('');
    const isEditor = typeof document !== 'undefined' && !!document.querySelector('.pb-editable-root');
    const code = React.useMemo(() => (isEditor ? (props?.code || 'ABCD-1234') : (props?.code || '')), [props?.code, isEditor]);
    React.useEffect(() => {
      let cancelled = false;
      (async () => {
        setError('');
        setQr(null);
        if (!code) return;
        try {
          const redeemUrl = `${window.location.origin}/redeem/${encodeURIComponent(code)}`;
          const dataUrl = await QRCode.toDataURL(redeemUrl, {
            margin: typeof props?.qrMargin === 'number' ? props.qrMargin : 1,
            scale: typeof props?.qrScale === 'number' ? props.qrScale : 5,
            errorCorrectionLevel: 'M',
          });
          if (!cancelled) setQr({ dataUrl, redeemUrl });
        } catch (e) {
          if (!cancelled) setError('QR konnte nicht erzeugt werden.');
        }
      })();
      return () => { cancelled = true; };
    }, [code, props?.qrScale, props?.qrMargin]);

    const style = resolveStyle(props);
    const alignCls = props?.textAlign === 'center' ? 'text-center' : props?.textAlign === 'right' ? 'text-right' : '';
    return (
      <div className={`mb-4 ${alignCls}`} style={style}>
        {qr ? (
          <img src={qr.dataUrl} alt="QR-Code" className="inline-block border p-1 bg-white" />
        ) : (
          <div className="inline-block text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1">
            {error || 'QR Vorschau'}</div>
        )}
        {qr?.redeemUrl && (
          <div className="mt-1">
            <a href={qr.redeemUrl} className="text-xs underline text-blue-600">Zum Einlösen</a>
          </div>
        )}
      </div>
    );
  }
  ,
  // Email personalisation: Button to open claim page
  claimButton: ({ props }) => {
    const style = resolveStyle(props);
    if (style && style.background) delete style.background; // avoid coloring wrapper instead of button
    const label = props?.label || 'Code anzeigen';
    const alignCls = props?.textAlign === 'center' ? 'text-center' : props?.textAlign === 'right' ? 'text-right' : '';
    const btnProps = {
      ...props,
      background: (props?.buttonBackground ?? props?.background),
      color: (props?.buttonColor ?? props?.color),
    };
    return (
      <div className={`mb-4 ${alignCls}`} style={style}>
        <StyledButton props={btnProps} onClick={()=>{}} />
        <div className="mt-1 text-[10px] text-slate-500">Öffnet die Code-Seite</div>
      </div>
    );
  }
};

function resolveStyle(props) {
  if (!props) return undefined;
  const style = {};
  if (props.background === 'surface') style.background = 'var(--pb-surface, #f8fafc)';
  if (props.background && props.background.startsWith('#')) style.background = props.background;
  if (props.color) style.color = props.color;
  if (props.textAlign) style.textAlign = props.textAlign;
  if (props.fontSize) style.fontSize = typeof props.fontSize === 'number' ? props.fontSize+'px' : props.fontSize;
  if (props.maxWidthPx) style.maxWidth = props.maxWidthPx + 'px';
  if (props.padding) {
    const map = { none: '0', sm: '0.5rem', md: '1rem', lg: '2rem', xl: '3rem' };
    style.padding = map[props.padding] || props.padding;
  }
  return style;
}

// Export for Craft.js integration
export function renderComponent(node) {
  const Component = Registry[node.type];
  if (!Component) return null;
  return <Component key={node.id} props={node.props} children={node.children} />;
}

// Export component names
export const componentTypes = Object.keys(Registry);
